import React, { useState, useContext } from "react";
import styled from "styled-components";
import { device, LAYOUT_CONSTANTS, useBreakpoints } from "../lib/breakpoints";
import {
  Col,
  Row,
  List,
  Avatar,
  Button,
  Card,
  Tag,
  Spin,
  Tooltip,
  Dropdown,
  message,
} from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  EditOutlined,
  SyncOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import LeaseModal from "../components/LeaseModal";
import { AuthContext } from "../context/AuthProvider";
import { trackEvent } from "../helpers/mixpanel";
import EmptyState from "../components/EmptyState";
import PageHeader from "../components/PageHeader";
import { StatCard } from "../components/shared";
import {
  listLeases,
  updateLease,
  getTenantIntentLink,
} from "../lib/api/leasesApi";
import { toDate } from "../lib/leaseUtils";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import unitModel from "../lib/firebase/unitModel";
import { listTenants } from "../lib/firebase/tenantModel";
import { propertyKeys } from "../lib/queryKeys";

const Leases = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { isMobile } = useBreakpoints();
  const [isLeaseModalVisible, setIsLeaseModalVisible] = useState(false);
  const [editLease, setEditLease] = useState(null);

  const {
    data: leases = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["leases", user?.uid],
    queryFn: listLeases,
    enabled: !!user?.uid,
    staleTime: 60_000,
  });

  const { data: units = [] } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  const { data: tenantsData } = useQuery({
    queryKey: ["tenants", user?.uid],
    queryFn: listTenants,
    enabled: !!user?.uid,
  });
  const tenants = tenantsData?.tenants || [];

  const activeLeasesCount = leases.filter((l) => l.status === "active").length;

  const expiringSoonCount = leases.filter((l) => {
    if (l.status !== "active") return false;
    const end = toDate(l.endDate);
    if (!end) return false;
    const days = (end - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days <= 60;
  }).length;

  const expiredCount = leases.filter((l) => l.status === "expired").length;

  const showLeaseModal = (lease = null) => {
    setEditLease(lease);
    setIsLeaseModalVisible(true);
  };

  const handleLeaseModalClose = () => {
    setIsLeaseModalVisible(false);
    setEditLease(null);
  };

  const handleLeaseModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["leases"] });
    trackEvent("Lease Created/Updated", { user_id: user?.uid });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "expired":
      case "terminated":
        return "error";
      case "ending":
      case "not_renewing":
        return "warning";
      case "pending_signature":
        return "processing";
      case "month_to_month":
        return "warning";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "expired":
      case "terminated":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "pending_signature":
        return <SyncOutlined spin style={{ color: "#1890ff" }} />;
      default:
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "Active";
      case "expired":
        return "Expired";
      case "terminated":
        return "Terminated";
      case "pending_signature":
        return "Pending Signature";
      case "month_to_month":
        return "Month-to-Month";
      case "draft":
        return "Draft";
      case "renewed":
        return "Renewed";
      case "ending":
        return "Ending";
      case "not_renewing":
        return "Not renewing";
      default:
        return status || "Unknown";
    }
  };

  const handleLeaseMenuAction = async (key, lease) => {
    try {
      if (key === "intent-link") {
        const { url } = await getTenantIntentLink(lease.id);
        await navigator.clipboard.writeText(url);
        message.success("Tenant intent link copied to clipboard");
        return;
      }
      if (key === "status-ending") {
        await updateLease(lease.id, { status: "ending" });
        message.success("Lease marked Ending");
        queryClient.invalidateQueries({ queryKey: ["leases", user?.uid] });
        return;
      }
      if (key === "status-not-renewing") {
        await updateLease(lease.id, { status: "not_renewing" });
        message.success("Lease marked Not renewing");
        queryClient.invalidateQueries({ queryKey: ["leases", user?.uid] });
      }
    } catch (e) {
      message.error(e?.message || "Action failed");
    }
  };

  const getTenantName = (lease) => {
    if (lease.tenantId) {
      const t = tenants.find((t) => t.id === lease.tenantId);
      if (t) {
        return (
          [t.firstName, t.lastName].filter(Boolean).join(" ") ||
          t.name ||
          t.email ||
          "Unnamed"
        );
      }
    }
    return lease.tenantEmail || "No tenant assigned";
  };

  const getUnitLabel = (lease) => {
    if (lease.unitId) {
      const u = units.find((u) => u.id === lease.unitId);
      if (u)
        return getUnitDisplayAddress(u) || u.unitNumber || u.name || "Unit";
    }
    return "No unit assigned";
  };

  const formatDate = (val) => {
    const d = toDate(val);
    return d ? d.toLocaleDateString() : "-";
  };

  const formatRent = (val) => {
    const n = Number(val);
    return isNaN(n) || n === 0 ? "-" : `$${n.toLocaleString()}/mo`;
  };

  return (
    <LeasesContainer className="leases-container">
      <PageHeader
        title="Leases"
        subtitle="Manage all active and past lease agreements"
        icon={FileTextOutlined}
      />

      <StatsSection>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard
              className="stats-card"
              title="Active Leases"
              value={activeLeasesCount}
              prefix={
                <FileTextOutlined style={{ color: "var(--color-primary)" }} />
              }
              valueStyle={{ color: "var(--color-primary)" }}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard
              className="stats-card"
              title="Expiring Soon"
              value={expiringSoonCount}
              prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14" }}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard
              className="stats-card"
              title="Expired"
              value={expiredCount}
              prefix={
                <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
              }
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Col>
        </Row>
      </StatsSection>

      <TableSection>
        <Card
          title={`Lease Details (${leases.length})`}
          style={{ borderRadius: "16px", position: "relative" }}
        >
          {isMobile ? (
            <Tooltip title="Add Lease">
              <AddLeaseButton
                type="primary"
                icon={
                  <PlusOutlined
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                }
                size="small"
                onClick={() => showLeaseModal()}
                aria-label="Add lease"
                $iconOnly
              />
            </Tooltip>
          ) : (
            <AddLeaseButton
              type="primary"
              icon={
                <PlusOutlined style={{ filter: "brightness(0) invert(1)" }} />
              }
              size="small"
              className="add-table-action"
              onClick={() => showLeaseModal()}
              aria-label="Add lease"
            >
              Add Lease
            </AddLeaseButton>
          )}

          {isLoading ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <EmptyState
              icon={<ExclamationCircleOutlined />}
              title="Could not load leases"
              description={error.message}
            />
          ) : leases.length === 0 ? (
            <EmptyState
              icon={
                <FileTextOutlined
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              }
              title="No Leases Yet"
              description="Leases are created automatically when you onboard tenants. You can also add one manually."
              ctaLabel="Add Lease"
              ctaAction={() => showLeaseModal()}
              ctaIcon={
                <PlusOutlined style={{ filter: "brightness(0) invert(1)" }} />
              }
            />
          ) : (
            <List
              dataSource={leases}
              renderItem={(lease) => {
                const endDate = toDate(lease.endDate);
                const daysLeft = endDate
                  ? Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))
                  : null;

                return (
                  <LeaseListItem>
                    <List.Item
                      actions={[
                        <Tooltip title="Edit lease" key="edit">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => showLeaseModal(lease)}
                          />
                        </Tooltip>,
                        <Dropdown
                          key="more"
                          trigger={["click"]}
                          menu={{
                            items: [
                              {
                                key: "intent-link",
                                label: "Copy tenant intent link",
                              },
                              { type: "divider" },
                              {
                                key: "status-ending",
                                label: "Mark Ending",
                              },
                              {
                                key: "status-not-renewing",
                                label: "Mark Not renewing",
                              },
                            ],
                            onClick: ({ key, domEvent }) => {
                              domEvent?.stopPropagation?.();
                              handleLeaseMenuAction(key, lease);
                            },
                          }}
                        >
                          <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            icon={getStatusIcon(lease.status)}
                            style={{ backgroundColor: "transparent" }}
                          />
                        }
                        title={
                          <LeaseTitle>
                            {getTenantName(lease)}
                            <Tag
                              color={getStatusColor(lease.status)}
                              style={{ marginLeft: 8 }}
                            >
                              {getStatusLabel(lease.status)}
                            </Tag>
                          </LeaseTitle>
                        }
                        description={
                          <LeaseDesc>
                            <span>{getUnitLabel(lease)}</span>
                            <span>
                              {formatDate(lease.startDate)} to{" "}
                              {formatDate(lease.endDate)}
                            </span>
                            <span>{formatRent(lease.rentAmount)}</span>
                            {daysLeft !== null &&
                              daysLeft > 0 &&
                              daysLeft <= 60 &&
                              lease.status === "active" && (
                                <Tag color="warning" style={{ marginLeft: 4 }}>
                                  {daysLeft}d left
                                </Tag>
                              )}
                          </LeaseDesc>
                        }
                      />
                    </List.Item>
                  </LeaseListItem>
                );
              }}
            />
          )}
        </Card>
      </TableSection>

      <LeaseModal
        isOpen={isLeaseModalVisible}
        onClose={handleLeaseModalClose}
        onSuccess={handleLeaseModalSuccess}
        editLease={editLease}
        ownerId={user?.uid}
      />
    </LeasesContainer>
  );
};

const LeasesContainer = styled.div`
  padding: 2rem;
  background: var(--color-bg-primary);
  min-height: 100vh;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
    max-width: 100%;
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
    max-width: 100%;
  }

  &.leases-container {
    background: var(--color-bg-primary) !important;
  }
`;

const StatsSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  .ant-card {
    --stat-title-size: 0.875rem;
    --stat-value-size: 1.5rem;
    --stat-suffix-size: 1.5rem;
    --stat-icon-size: 1.5rem;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: var(--stat-title-size) !important;
    line-height: 1.25;
    font-weight: 600;
    color: var(--color-text-light);
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-suffix,
  .ant-statistic-content-prefix {
    font-size: var(--stat-suffix-size) !important;
    font-weight: 700;
    line-height: 1.2;
  }

  .ant-statistic .ant-statistic-content-prefix svg,
  .ant-statistic .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
    width: var(--stat-icon-size) !important;
    height: var(--stat-icon-size) !important;
  }

  @media (max-width: 768px) {
    .ant-card {
      --stat-title-size: 0.8rem;
      --stat-value-size: 1.25rem;
      --stat-suffix-size: 1.25rem;
      --stat-icon-size: 1.25rem;
    }
  }
`;

const TableSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  .ant-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
  }

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    min-height: 56px;
    padding: 0;
  }

  .ant-card-head-title {
    font-family: var(--font-primary);
    font-weight: 700;
    font-size: 1.125rem;
    color: #ffffff !important;
    padding: 1rem 1.5rem;
  }

  .ant-card-body {
    padding: 1.25rem 1.5rem 1.5rem;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 1rem;
    }
  }
`;

const AddLeaseButton = styled(Button)`
  &&.ant-btn.ant-btn-primary {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
    color: #ffffff !important;
    border-radius: 8px;
    font-family: var(--font-secondary);
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.2px;
    height: ${(props) => (props.$iconOnly ? "32px" : "36px")};
    width: ${(props) => (props.$iconOnly ? "32px" : "auto")};
    min-width: ${(props) => (props.$iconOnly ? "32px" : "auto")};
    padding: ${(props) => (props.$iconOnly ? "0" : "2px 10px")};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);
  }

  &&.ant-btn.ant-btn-primary span,
  &&.ant-btn.ant-btn-primary .ant-btn-icon,
  &&.ant-btn.ant-btn-primary .anticon {
    color: #ffffff !important;
  }

  &&.ant-btn.ant-btn-primary:hover,
  &&.ant-btn.ant-btn-primary:focus {
    background: var(--color-primary-dark) !important;
    border-color: var(--color-primary-dark) !important;
    color: #ffffff !important;
  }
`;

const LeaseListItem = styled.div`
  .ant-list-item {
    padding: 12px 0;
    border-bottom: 1px solid var(--color-border, #f0f0f0);
  }
`;

const LeaseTitle = styled.span`
  font-weight: 600;
  font-family: var(--font-secondary);
  display: inline-flex;
  align-items: center;
`;

const LeaseDesc = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.875rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    flex-direction: column;
    gap: 4px;
  }
`;

export default Leases;
